/**
 * companion.js — Camlin Companion embed entry (doc 116 §2).
 * Async script tag; reads data-camlin-companion-key from its own tag; no layout shift.
 *
 * Host usage (cache-busted per ios-cache-busting.mdc):
 * <script
 *   src="https://companion.camlinconnect.com.au/companion.js?v=<hash>"
 *   data-camlin-companion-key="cmp_<tenantId>_<base32>"
 *   async
 * ></script>
 */

import { bootCompanion, findCompanionScriptTag } from './boot';
import { CAMLIN_COMPANION_ATTR } from './attributes';
import { onCompanionSpeakingChange, requestCompanionStopSpeaking } from './speakingControl';

declare global {
  interface Window {
    /** Populated from this module's exports — see the note beside them. */
    CamlinCompanion?: {
      boot: typeof bootCompanion;
      version: string;
      ask: typeof askCompanion;
      stopSpeaking: typeof stopSpeaking;
      onSpeakingChange: typeof onSpeakingChange;
    };
  }
}

/**
 * Cut the avatar off mid-utterance (2.11.0). Same window-event contract the
 * in-panel Stop button and the SDK's SpeechPlaybackControl use, so a host
 * page can wire its own Stop affordance without reaching into the widget.
 */
function stopSpeaking(): void {
  requestCompanionStopSpeaking('host-api');
}

/**
 * Subscribe to the avatar starting/stopping speech (2.11.0). Returns the
 * unsubscribe function.
 */
function onSpeakingChange(listener: (speaking: boolean) => void): () => void {
  return onCompanionSpeakingChange(listener);
}

/**
 * "Ask the avatar about this" convenience (bundle 2.9.0, nua-dot-com
 * DEMO_STRATEGY §4.3): dispatches the same `camlin-companion:ask` window
 * event the widget listens for. `opts.source` (a host section id) rides in
 * the event detail so host pages can correlate their own analytics; host
 * pages may also feature-detect this function to light up shortcut buttons.
 */
function askCompanion(prompt: string, opts?: { source?: string }): void {
  window.dispatchEvent(
    new CustomEvent('camlin-companion:ask', { detail: { prompt, source: opts?.source } })
  );
}

// Injected at build time from package.json (vite.config.companion.ts
// `define`) so one number names both the SDK and the hosted embed —
// companion-sdk-packaging-plan §3. The fallback only appears in dev
// serves that skip the define.
declare const __COMPANION_EMBED_VERSION__: string | undefined;
const COMPANION_EMBED_VERSION =
  typeof __COMPANION_EMBED_VERSION__ !== 'undefined' ? __COMPANION_EMBED_VERSION__ : '0.0.0-dev';

/**
 * There are two ways to embed the Companion and only one of them uses a keyed
 * script tag; the other passes its whole config through a
 * `<camlin-companion data-config="…">` element, which boots itself when the
 * element upgrades. Auto-booting from the script tag regardless meant every
 * page of every element-based host logged "boot refused: invalid_site_key" on
 * load — an alarming message for a page where nothing was wrong.
 */
function scheduleBoot(): void {
  const script = findCompanionScriptTag();
  const keyed = !!script?.getAttribute(CAMLIN_COMPANION_ATTR.key);
  if (!keyed && document.querySelector('camlin-companion')) {
    return;
  }
  void bootCompanion({ scriptElement: script }).then((result) => {
    if (!result.ok) {
      console.warn('[Camlin Companion] boot refused:', result.refusalReason, result.message);
    }
  });
}

if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleBoot, { once: true });
  } else {
    scheduleBoot();
  }
}

/**
 * The public embed API, as named exports.
 *
 * These ARE `window.CamlinCompanion`: the bundle is built as an IIFE under
 * that global name, so Vite assigns this module's exports to it. This file
 * used to also set `window.CamlinCompanion = { boot, version, ask }` in its
 * body, which looked like the API and was then silently overwritten by the
 * exports object the moment the bundle finished evaluating — so the shipped
 * global carried `bootCompanion` and friends but no `ask` and no `version`,
 * and every host feature-detecting `CamlinCompanion.ask` concluded the widget
 * did not support shortcuts. Declaring them as exports is what makes the
 * documented API and the delivered one the same thing.
 */
export const boot = bootCompanion;
export const version = COMPANION_EMBED_VERSION;
export const ask = askCompanion;
export { stopSpeaking, onSpeakingChange };

export { bootCompanion, findCompanionScriptTag };
export {
  COMPANION_SPEAKING_EVENT,
  COMPANION_STOP_SPEAKING_EVENT,
  COMPANION_STOP_SPEAKING_LABEL
} from './speakingControl';
export * from './types';
export * from './siteKey';
export * from './attributes';
